import {
  ArrowRight,
  BriefcaseBusiness,
  Coins,
  Compass,
  Dumbbell,
  GraduationCap,
  Heart,
  History,
  Plus,
  ShieldCheck,
  Sparkles
} from "lucide-react";
import { useState } from "react";
import { Button, Card, EmptyState, PageHeader, SectionHeading } from "../components/ui";
import { useApp } from "../context/AppContext";
import { formatDate } from "../lib/format";
import type { Dimension, Skill } from "../types";

const dimensionIcons = {
  "attr-career": BriefcaseBusiness,
  "attr-wealth": Coins,
  "attr-experience": Compass,
  "attr-physique": Dumbbell
} as const;

export function GrowthPage() {
  const { data, openModal } = useApp();
  const [detailSkill, setDetailSkill] = useState<Skill | null>(null);
  if (!data) return null;

  return (
    <div className="page page--growth growth-page">
      <PageHeader
        eyebrow="GROWTH · 人物面板"
        title="人物面板"
        description={<strong className="page-header__motto">当你度过七层孤独，才能成为真正的强者</strong>}
        actions={<Button onClick={() => openModal("skill")} icon={<Plus size={16} />}>添加技能</Button>}
      />

      <Card className="digital-avatar-card">
        <DigitalAvatar
          name={data.profile?.display_name || "玩家"}
          xp={data.skills.reduce((sum, skill) => sum + Number(skill.total_xp ?? 0), 0)}
        />
        <div>
          <span className="eyebrow">DIGITAL SELF · 数码分身</span>
          <h2>{data.profile?.display_name || "你的数码小人"}</h2>
          <p className="avatar-summary avatar-summary--gold">你问我是怎样的强者？——我不是站在巅峰才成为强者，而是因为无论命运把我打入多少次深渊，我都会从深渊中重新站起；当有一天我真正站上巅峰，我身后的每一道伤痕，都将成为我俯视命运的王座。</p>
        </div>
        <div className="avatar-signal-list">
          {data.dimensions.map((item) => (
            <span key={item.id} className={item.evidence_count ? "active" : ""}>
              <i />{item.title} · {item.stage_name} {item.grade}
            </span>
          ))}
        </div>
      </Card>

      <section className="skill-tree-section" aria-label="技能树">
        <SectionHeading
          title="技能树"
          description="按分类生长的技能树。点开节点看详情，新增的技能会自动挂到树上。"
        />
        <div className="skill-tree">
          {[
            { key: "WORK", label: "工作之树", skills: data.skills.filter((skill) => skill.kind === "SKILL" && skill.sphere === "WORK") },
            { key: "LIFE", label: "生活之树", skills: data.skills.filter((skill) => skill.kind === "SKILL" && skill.sphere === "LIFE") },
            { key: "INTEREST", label: "兴趣之树", skills: data.skills.filter((skill) => skill.kind === "INTEREST") }
          ].filter((group) => group.skills.length).map((group) => (
            <div key={group.key} className="skill-tree__branch">
              <div className="skill-tree__branch-label"><i />{group.label}</div>
              <div className="skill-tree__nodes">
                {group.skills.map((skill) => (
                  <button
                    key={skill.id}
                    className={`skill-node${skill.level > 0 ? " skill-node--gold" : ""}`}
                    onClick={() => setDetailSkill(skill)}
                  >
                    <span className="skill-node__gem">◆</span>
                    <strong>{skill.title}</strong>
                    <small>{skill.level_title}</small>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="growth-journey-section" aria-label="个人长期成长">
        <SectionHeading
          title="四维状态"
          description="阶段看现实突破，E–A 表示当前阶段内的位置；每一次提升都来自真实成果。"
          action={
            <div className="growth-system-note">
              <ShieldCheck size={15} /> 日常少量积累 · 跨阶段看成果
            </div>
          }
        />
        <div className="dimension-grid">
          {data.dimensions.map((dimension) => {
            const Icon = dimensionIcons[dimension.id];
            return (
              <article className={`growth-dimension growth-dimension--${dimension.id.replace("attr-", "")}`} key={dimension.id}>
                <header className="growth-dimension__header">
                  <div className="growth-dimension__identity">
                    <span><Icon size={18} /></span>
                    <div>
                      <small>第 {dimension.stage_number} / {dimension.stage_count} 阶段</small>
                      <h3>{dimension.title} · {dimension.stage_name}</h3>
                    </div>
                  </div>
                  <div className="growth-grade-badge" aria-label={`段内评级 ${dimension.grade}`}>
                    <strong>{dimension.grade}</strong>
                    <span>段内评级</span>
                  </div>
                </header>

                <div className="growth-dimension__value">
                  <strong>{formatGrowthValue(dimension)}</strong>
                  <span>{dimension.unit === "CNY" ? "当前人民币资产" : "累计长期经验"}</span>
                </div>

                <div className="growth-dimension__progress" aria-label={`段内进度 ${dimension.stage_progress}%`}>
                  <div>
                    <span>阶段进度</span>
                    <strong>{dimension.stage_progress} / 100</strong>
                  </div>
                  <div className="growth-grade-track"><i style={{ width: `${dimension.stage_progress}%` }} /></div>
                </div>

                <div className="growth-dimension__next">
                  <strong>{nextStageCopy(dimension)}</strong>
                  <span>{dimension.basis}</span>
                </div>
              </article>
            );
          })}
        </div>
        <div className="growth-grade-legend" aria-label="属性等级说明">
          {[
            ["E", "探索"],
            ["D", "起步"],
            ["C", "成长"],
            ["B", "精进"],
            ["A", "大师"]
          ].map(([grade, label]) => (
            <span key={grade}><strong>{grade}</strong>{label}</span>
          ))}
        </div>
      </section>

      <div className="growth-summary">
        <div className="growth-summary__item"><span>技能</span><strong>{data.skills.filter((item) => (item.kind ?? "SKILL") === "SKILL").length}</strong></div>
        <div className="growth-summary__item"><span>证据事件</span><strong>{data.skills.reduce((sum, skill) => sum + Number(skill.evidence_count), 0)}</strong></div>
        <div className="growth-summary__item"><span>累计经验</span><strong>{data.skills.reduce((sum, skill) => sum + Number(skill.total_xp), 0)}</strong></div>
        <div className="growth-summary__item"><span>兴趣</span><strong>{data.skills.filter((item) => item.kind === "INTEREST").length}</strong></div>
      </div>

      {detailSkill && (
        <div className="modal-backdrop" onClick={() => setDetailSkill(null)}>
          <div className="modal-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="modal__header">
              <div>
                <span className="eyebrow">SKILL NODE · 技能节点</span>
                <h2>{detailSkill.title}</h2>
                <p>{detailSkill.description || "还没有描述"}</p>
              </div>
              <button className="modal__close" onClick={() => setDetailSkill(null)}>✕</button>
            </div>
            <div className="modal__body skill-detail">
              <div className="skill-detail__stats">
                <div><strong>{detailSkill.level_title}</strong><span>当前等级</span></div>
                <div><strong>{detailSkill.total_xp}</strong><span>总经验 XP</span></div>
                <div><strong>{detailSkill.evidence_count}</strong><span>成长证据</span></div>
              </div>
              <div className="skill-detail__bar"><i style={{ width: `${Math.min(100, Math.max(4, Number(detailSkill.level_progress ?? 0)))}%` }} /></div>
              <p className="skill-detail__next">距下一级还差 {detailSkill.xp_to_next_level} XP——完成关联任务或学习打卡即可获得。</p>
            </div>
            <div className="modal__footer">
              <Button variant="ghost" onClick={() => setDetailSkill(null)}>关闭</Button>
              <Button icon={<GraduationCap size={15} />} onClick={() => { setDetailSkill(null); window.location.href = "/plans"; }}>去学习</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const cnyFormatter = new Intl.NumberFormat("zh-CN", {
  style: "currency",
  currency: "CNY",
  maximumFractionDigits: 0
});

function formatGrowthValue(dimension: Dimension) {
  return dimension.unit === "CNY"
    ? cnyFormatter.format(dimension.value)
    : `${Math.round(dimension.value).toLocaleString("zh-CN")} XP`;
}

function nextStageCopy(dimension: Dimension) {
  if (!dimension.next_stage_name || dimension.remaining === null) {
    return "已进入最高长期参照，继续用真实成果拓宽上限";
  }
  const remaining = dimension.unit === "CNY"
    ? cnyFormatter.format(dimension.remaining)
    : `${Math.round(dimension.remaining).toLocaleString("zh-CN")} XP`;
  return `距离「${dimension.next_stage_name}」还需 ${remaining}`;
}

function DigitalAvatar({ name, xp }: { name: string; xp: number }) {
  const level = Math.floor(Math.sqrt(Math.max(0, xp) / 40));
  return (
    <div className="digital-avatar digital-avatar--knight digital-avatar--cyber" aria-label={`${name}的数码分身`}>
      <span className="cyber-ring" />
      <img src="/earth-online-knight-v3.png" alt="赛博骑士分身" draggable={false} />
      <span className="cyber-hud cyber-hud--level">LV {level}</span>
      <span className="cyber-hud cyber-hud--xp">XP {xp}</span>
    </div>
  );
}

function summarizePersonality(
  profile: { energy: number | null; focus: number | null; mood: number | null } | null | undefined,
  skills: Array<{ title: string }>,
  orders: Array<{ status: string }>
) {
  const parts: string[] = [];
  if (profile?.focus != null && profile.focus >= 4) parts.push("目标感强，认准的事就往前冲");
  else if (profile?.focus != null && profile.focus <= 2) parts.push("最近注意力分散，适合先收拢到一件事上");
  if (profile?.energy != null && profile.energy >= 4) parts.push("精力在线");
  else if (profile?.energy != null && profile.energy <= 2) parts.push("能量偏低，记得留时间恢复");
  if (profile?.mood != null && profile.mood >= 4) parts.push("状态不错");
  else if (profile?.mood != null && profile.mood <= 2) parts.push("情绪需要关照");
  if (skills.length) parts.push(`正在打磨「${skills[0]?.title ?? "技能"}」等 ${skills.length} 项技能`);
  const activeOrders = orders.filter((order) => !["COMPLETED", "CANCELLED"].includes(order.status)).length;
  if (activeOrders) parts.push(`在推进 ${activeOrders} 个订单`);
  if (!parts.length) parts.push("正在积累第一份成长证据，每一天都算数");
  return parts.join("，") + "。";
}
